import { token } from "../auth/keycloak";
import type {
  ActionEmailResult, AdminPolygon, AdminZone, ApiTokenSecret, ApiTokenSummary,
  CatalogResponse, LandUseExtras, LandUsePointResponse, LandUseZoneResponse,
  PointLayersResponse, PointResponse, Profile, ProfileUpdate, SoilSource, WhoAmI,
  WeatherDailyResponse, WeatherSource,
  ZoneLayersResponse, ZoneResponse,
} from "../types/api";

const BASE = import.meta.env.VITE_API_BASE_URL || "";

async function request<T>(
  path: string, params?: Record<string, string | number | undefined>,
): Promise<T> {
  const url = new URL(BASE + path, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = { Accept: "application/json" };
  const t = token();
  if (t) headers.Authorization = `Bearer ${t}`;

  const r = await fetch(url.toString(), { headers });
  if (r.status === 429) {
    const body = await r.json().catch(() => ({}));
    throw new RateLimitError(body.message ?? "Rate limit exceeded", body.retry_after_seconds ?? 60);
  }
  if (!r.ok) {
    throw new Error(`API ${r.status}: ${await r.text()}`);
  }
  return r.json() as Promise<T>;
}

async function mutate<T>(
  method: "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const url = new URL(BASE + path, window.location.origin);
  const headers: Record<string, string> = { Accept: "application/json" };
  const t = token();
  if (t) headers.Authorization = `Bearer ${t}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const r = await fetch(url.toString(), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (r.status === 204) return undefined as T;
  if (r.status === 429) {
    const data = await r.json().catch(() => ({}));
    throw new RateLimitError(data.message ?? "Rate limit exceeded", data.retry_after_seconds ?? 60);
  }
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(
      (data && (data.message || data.error)) || `API ${r.status}`,
    );
  }
  return data as T;
}

export class RateLimitError extends Error {
  retryAfter: number;
  constructor(msg: string, retryAfter: number) {
    super(msg);
    this.retryAfter = retryAfter;
  }
}

type PointOpts = {
  /** Filter to a subset of layers, e.g. ["clay", "soc"]. Default = all active. */
  layers?: string[];
  year?: number;
  /** Multi-year weather series — populates `series` in the response. */
  years?: number[];
  /** Snap-to-nearest-valid-data radius in metres (0..50 000). */
  buffer?: number;
  /** Landuse-only: whether to compute forest-stand polygons. Off saves
   *  ~50–500 KB / ~100–1000 ms per request. Default: backend's default (true). */
  includeGeometry?: boolean;
  /** Soil-only: provider tag. Defaults to "soilgrids" on the backend. */
  source?: SoilSource;
  /** Weather-only: data source. Defaults to "terraclimate" on the backend. */
  weatherSource?: WeatherSource;
};

type ZoneOpts = PointOpts & {
  withLevel?: string;
};

const csv = (xs?: number[] | string[]) =>
  xs && xs.length ? xs.join(",") : undefined;

const pointParams = (lat: number, lon: number, opts: PointOpts = {}) => ({
  lat,
  lon,
  layers: csv(opts.layers),
  year: opts.year,
  years: csv(opts.years),
  buffer: opts.buffer && opts.buffer > 0 ? opts.buffer : undefined,
});

const zoneParams = (lat: number, lon: number, opts: ZoneOpts = {}) => ({
  ...pointParams(lat, lon, opts),
  withLevel: opts.withLevel,
});

export const api = {
  whoami:  () => request<WhoAmI>("/api/auth/me"),
  catalog: () => request<CatalogResponse>("/api/catalog"),

  // Profile + API tokens. All require a fresh user-session JWT; an API
  // token presented to any of these returns 400.
  profile: {
    get:    () => request<Profile>("/api/profile"),
    update: (body: ProfileUpdate) => mutate<Profile>("PATCH", "/api/profile", body),
    requestPasswordReset: (redirectUri?: string) =>
      mutate<ActionEmailResult>("POST", "/api/profile/password-reset",
        { redirect_uri: redirectUri ?? null }),
    requestVerifyEmail: () =>
      mutate<ActionEmailResult>("POST", "/api/profile/verify-email"),

    listTokens:   () => request<{ tokens: ApiTokenSummary[] }>("/api/profile/tokens"),
    createToken:  (name: string) =>
      mutate<ApiTokenSecret>("POST", "/api/profile/tokens", { name }),
    rotateToken:  (id: string) =>
      mutate<ApiTokenSecret>("POST", `/api/profile/tokens/${id}/rotate`),
    revokeToken:  (id: string) =>
      mutate<void>("DELETE", `/api/profile/tokens/${id}`),
  },

  admin:   (lat: number, lon: number, withLevel?: string) =>
    request<AdminZone>("/api/admin", { lat, lon, withLevel }),
  adminPolygon: (gid: string, level: number) =>
    request<AdminPolygon>("/api/admin/polygon", { gid, level }),

  soil: {
    point: (lat: number, lon: number, opts?: PointOpts) =>
      request<PointLayersResponse>("/api/soil/point", {
        ...pointParams(lat, lon, opts), source: opts?.source,
      }),
    zone:  (lat: number, lon: number, opts?: ZoneOpts) =>
      request<ZoneLayersResponse>("/api/soil/zone", {
        ...zoneParams(lat, lon, opts), source: opts?.source,
      }),
  },
  weather: {
    point: (lat: number, lon: number, opts?: PointOpts) =>
      request<PointLayersResponse>("/api/weather/point", {
        ...pointParams(lat, lon, opts), source: opts?.weatherSource,
      }),
    series:(lat: number, lon: number, years: number[], opts?: Omit<PointOpts, "years">) =>
      request<PointLayersResponse>("/api/weather/series", {
        ...pointParams(lat, lon, { ...opts, years }), source: opts?.weatherSource,
      }),
    zone:  (lat: number, lon: number, opts?: ZoneOpts) =>
      request<ZoneLayersResponse>("/api/weather/zone", {
        ...zoneParams(lat, lon, opts), source: opts?.weatherSource,
      }),
    daily: (lat: number, lon: number, opts: {
      dateStart: string;
      dateEnd: string;
      layers?: string[];
      buffer?: number;
      source?: WeatherSource;
    }) => {
      const qs = new URLSearchParams({
        lat: String(lat), lon: String(lon),
        date_start: opts.dateStart, date_end: opts.dateEnd,
        source: opts.source ?? "era5",
      });
      if (opts.layers && opts.layers.length > 0) qs.set("layers", opts.layers.join(","));
      if (opts.buffer !== undefined) qs.set("buffer", String(opts.buffer));
      return request<WeatherDailyResponse>(`/api/weather/daily?${qs.toString()}`);
    },
  },
  climate: {
    point: (lat: number, lon: number, opts?: PointOpts) =>
      request<PointLayersResponse>("/api/climate/point", pointParams(lat, lon, opts)),
    zone:  (lat: number, lon: number, opts?: ZoneOpts) =>
      request<ZoneLayersResponse>("/api/climate/zone", zoneParams(lat, lon, opts)),
  },
  landuse: {
    point: (lat: number, lon: number, opts?: PointOpts) =>
      request<LandUsePointResponse>("/api/landuse/point", {
        ...pointParams(lat, lon, opts),
        include_geometry: opts?.includeGeometry === false ? "false" : undefined,
      }),
    zone:  (lat: number, lon: number, opts?: ZoneOpts) =>
      request<LandUseZoneResponse>("/api/landuse/zone", {
        ...zoneParams(lat, lon, opts),
        include_geometry: opts?.includeGeometry === false ? "false" : undefined,
      }),
    /** Slow (~1–4 s) upstream proxy for Meta canopy height. Fire in parallel
     *  with the rest of the Land-use section so this never blocks the page. */
    extras: (lat: number, lon: number) =>
      request<LandUseExtras>("/api/landuse/extras", { lat, lon }),
  },

  /** Combined fan-out: fires all three domain endpoints in parallel plus an
   *  admin lookup, then merges to the legacy {soil, weather, climate, admin,
   *  ...} shape that ExplorePage consumes. The backend deliberately has no
   *  combined endpoint — clients pay per domain, and the parallel fan-out
   *  matches the old single-call cost in aggregate. */
  combinedPoint: async (lat: number, lon: number, opts: ZoneOpts = {}): Promise<PointResponse> => {
    const [soil, weather, climate, landuse, admin] = await Promise.all([
      api.soil.point(lat, lon, opts),
      api.weather.point(lat, lon, opts),
      api.climate.point(lat, lon, opts),
      api.landuse.point(lat, lon, opts),
      opts.withLevel ? api.admin(lat, lon, opts.withLevel).catch(() => null) : Promise.resolve(null),
    ]);
    return {
      lat, lon,
      admin: admin ?? null,
      soil: soil.layers,
      weather: weather.layers,
      weather_series: weather.series ?? null,
      climate: climate.layers,
      landuse: landuse.layers,
      forest_patch: landuse.forest_patch ?? null,
      regrowth_patch: landuse.regrowth_patch ?? null,
      soil_texture: soil.soil_texture ?? null,
      warnings: mergeWarnings(
        soil.warnings, weather.warnings, climate.warnings, landuse.warnings,
      ),
    };
  },

  /** Combined zonal fan-out — same idea as `combinedPoint`. Every domain
   *  endpoint resolves its own admin polygon; we keep the one from `soil` as
   *  the canonical echo since soil's `withLevel` matches the caller's intent. */
  combinedZone: async (lat: number, lon: number, opts: ZoneOpts = {}): Promise<ZoneResponse> => {
    const [soil, weather, climate, landuse] = await Promise.all([
      api.soil.zone(lat, lon, opts),
      api.weather.zone(lat, lon, opts),
      api.climate.zone(lat, lon, opts),
      api.landuse.zone(lat, lon, opts),
    ]);
    return {
      lat, lon,
      admin: soil.admin,
      soil: soil.layers,
      weather: weather.layers,
      weather_series: weather.series ?? null,
      climate: climate.layers,
      landuse: landuse.layers,
      forest_change: landuse.forest_change ?? null,
      forest_patches: landuse.forest_patches ?? null,
      regrowth_patches: landuse.regrowth_patches ?? null,
      soil_texture: soil.soil_texture ?? null,
      warnings: mergeWarnings(
        soil.warnings, weather.warnings, climate.warnings, landuse.warnings,
      ),
    };
  },
};

function mergeWarnings(...lists: (string[] | null | undefined)[]): string[] | null {
  const out = lists.flatMap((l) => l ?? []);
  return out.length ? out : null;
}
